import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {LeafType, isBenchlingTypeDefList} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {BioentitySearchInputWithItems, BioentitySearchInputProps} from 'inventory/BioentitySearchInput';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';

import {ValueInputProps} from './ValueInputTypes';
import valuesToItemsArray, {itemsToValuesArray} from './valuesToItemsArray';

type BaseBioentityValueInputProps = ValueInputProps<BioentityLeafType> &
  Pick<BioentitySearchInputProps, 'bioentityTypes'>;

type BioentityLeafType =
  | LeafType.BIOENTITY
  | LeafType.BASIC_FOLDER_ITEM
  | LeafType.MIXTURE
  | LeafType.MOLECULE
  | LeafType.PROTEIN
  | LeafType.SEQUENCE;

function BaseBioentityValueInput({
  bioentityTypes,
  onChange,
  schemaId,
  typeDef,
  valueInput,
  disabled,
  allowAutoFocus,
  hideSelectedResults,
}: BaseBioentityValueInputProps): JSX.Element {
  const value = valueInput.isValid ? valueInput.value : null;

  function handleOnChange(items: Array<Item>): void {
    const itemIds = itemsToValuesArray(items);

    if (isBenchlingTypeDefList(typeDef)) {
      onChange(itemIds);
    } else {
      onChange(itemIds[0] ?? null);
    }
  }

  return (
    <BioentitySearchInputWithItems
      shouldAllowMultiple={isBenchlingTypeDefList(typeDef)}
      onChange={handleOnChange}
      tagSchemaOrInterfaceId={schemaId}
      selectedItems={valuesToItemsArray(value)}
      bioentityTypes={bioentityTypes}
      disabled={disabled}
      isReadOnly={disabled} // TODO(BNCH-57668): keep disabled and isReadOnly separate when behavior is fixed
      autoFocus={allowAutoFocus}
      hideSelectedResults={hideSelectedResults}
    />
  );
}

/**
 * @package
 */
export function BioentityValueInput(props: ValueInputProps<LeafType.BIOENTITY>): JSX.Element {
  return <BaseBioentityValueInput {...props} />;
}

/**
 * @package
 */
export function BasicFolderItemValueInput(props: ValueInputProps<LeafType.BASIC_FOLDER_ITEM>): JSX.Element {
  return <BaseBioentityValueInput {...props} bioentityTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]} />;
}

/**
 * @package
 */
export function MixtureValueInput(props: ValueInputProps<LeafType.MIXTURE>): JSX.Element {
  return <BaseBioentityValueInput {...props} bioentityTypes={[ModelTypeConstants.MIXTURE]} />;
}

/**
 * @package
 */
export function MoleculeValueInput(props: ValueInputProps<LeafType.MOLECULE>): JSX.Element {
  return <BaseBioentityValueInput {...props} bioentityTypes={[ModelTypeConstants.MOLECULE]} />;
}

/**
 * @package
 */
export function ProteinValueInput(props: ValueInputProps<LeafType.PROTEIN>): JSX.Element {
  return <BaseBioentityValueInput {...props} bioentityTypes={[ModelTypeConstants.PROTEIN]} />;
}

/**
 * @package
 */
export function SequenceValueInput(props: ValueInputProps<LeafType.SEQUENCE>): JSX.Element {
  return <BaseBioentityValueInput {...props} bioentityTypes={[ModelTypeConstants.SEQUENCE]} />;
}
